// CMS Security Settings (2FA)
import React, { useState, useEffect } from 'react';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Shield, Key, Smartphone, Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSSecurity = () => {
  const { token, admin, refreshAdmin } = useCMS();
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [isDisableOpen, setIsDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    checkStatus();
  }, [admin]);

  const checkStatus = () => {
    // Check if admin has 2FA enabled
    setIs2FAEnabled(admin?.two_factor_enabled || false);
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/cms/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ method: 'totp' })
      });

      if (res.ok) {
        const data = await res.json();
        setSetupData(data);
        setIsSetupOpen(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to setup 2FA');
      }
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/cms/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ code: verifyCode })
      });

      if (res.ok) {
        const data = await res.json();
        setBackupCodes(data.backup_codes);
        setIs2FAEnabled(true);
        toast.success('2FA enabled successfully!');
        // Don't close dialog - show backup codes
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/cms/auth/2fa?password=${encodeURIComponent(disablePassword)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setIs2FAEnabled(false);
        setIsDisableOpen(false);
        setDisablePassword('');
        toast.success('2FA disabled');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to disable 2FA');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const closeSetupDialog = () => {
    setIsSetupOpen(false);
    setSetupData(null);
    setVerifyCode('');
    setBackupCodes([]);
  };

  return (
    <div className="space-y-6" data-testid="cms-security">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield className="h-6 w-6" /> Security Settings
        </h1>
        <p className="text-slate-400">Manage your account security and two-factor authentication</p>
      </div>

      {/* 2FA Section */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="h-6 w-6 text-blue-400" />
              <div>
                <CardTitle className="text-white">Two-Factor Authentication</CardTitle>
                <CardDescription>Add an extra layer of security to your account</CardDescription>
              </div>
            </div>
            <Badge className={is2FAEnabled ? 'bg-green-500' : 'bg-slate-600'}>
              {is2FAEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {is2FAEnabled ? (
            <div className="space-y-4">
              <p className="text-sm text-green-400 flex items-center gap-2">
                <Check className="h-4 w-4" />
                Your account is protected with two-factor authentication
              </p>
              <Dialog open={isDisableOpen} onOpenChange={setIsDisableOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    Disable 2FA
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-gray-200 text-gray-900">
                  <DialogHeader>
                    <DialogTitle className="text-white">Disable Two-Factor Authentication</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleDisable2FA} className="space-y-4">
                    <div className="p-4 bg-red-900/30 rounded-lg border border-red-700">
                      <p className="text-sm text-red-400 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        This will make your account less secure
                      </p>
                    </div>
                    <div>
                      <Label>Confirm your password</Label>
                      <Input
                        type="password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        className="bg-slate-900 border-slate-600"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsDisableOpen(false)}>Cancel</Button>
                      <Button type="submit" variant="destructive" disabled={loading}>
                        {loading ? 'Disabling...' : 'Disable 2FA'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Two-factor authentication adds an extra layer of security by requiring a code from your authenticator app when logging in.
              </p>
              <Button onClick={handleSetup2FA} disabled={loading}>
                <Key className="h-4 w-4 mr-2" />
                {loading ? 'Setting up...' : 'Enable 2FA'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={isSetupOpen} onOpenChange={closeSetupDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {backupCodes.length > 0 ? 'Save Your Backup Codes' : 'Setup Two-Factor Authentication'}
            </DialogTitle>
          </DialogHeader>

          {backupCodes.length > 0 ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-900/30 rounded-lg border border-yellow-700">
                <p className="text-sm text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Save these backup codes securely. They won't be shown again!
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <div key={i} className="font-mono text-sm bg-slate-900 p-2 rounded text-center text-white">
                    {code}
                  </div>
                ))}
              </div>
              <Button 
                className="w-full" 
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
              >
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy All Codes'}
              </Button>
              <Button variant="outline" className="w-full" onClick={closeSetupDialog}>
                I've Saved My Codes
              </Button>
            </div>
          ) : setupData ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-slate-400 mb-4">
                  Scan this code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.provisioning_uri)}`}
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-xs text-slate-500 mb-2">Or enter this code manually:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="bg-slate-900 px-3 py-1 rounded text-sm font-mono text-white">
                    {setupData.secret}
                  </code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(setupData.secret)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div>
                  <Label>Enter code from authenticator app</Label>
                  <Input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-slate-900 border-slate-600 text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading || verifyCode.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
                </Button>
              </form>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Session Info */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Current Session</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Email</span>
              <span className="text-white">{admin?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Role</span>
              <Badge variant="outline">{admin?.role}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">2FA Status</span>
              <Badge className={is2FAEnabled ? 'bg-green-500' : 'bg-slate-600'}>
                {is2FAEnabled ? 'Protected' : 'Not Protected'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CMSSecurity;
